import React, { useState } from "react";
import config from "../../Config";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { Form, Spinner } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import FeeSystemMenu from "./FeeSystemMenu";
import RightsCheck from "../General/RightsCheck";

/**
 * Reverts a posted bank scroll: tblADLedger, tblChalanHead (unpaid), deletes tblScrollEntry row.
 * Backend finds fee lines by challan + bench + receipt from the scroll row (no extra DB columns).
 */
function RollbackBankScroll() {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [Challan_No, setChallan_No] = useState("");
  const [Student_ID, setStudent_ID] = useState("");
  const [Registration_No, setRegistration_No] = useState("");
  const [AdminRemarks, setAdminRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [showHelp, setShowHelp] = useState(true);

  document.title = "Rollback Bank Scroll";

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }

    const sid = parseInt(String(Student_ID).trim(), 10);
    const hasSid = !Number.isNaN(sid) && sid > 0;
    const reg = (Registration_No || "").trim();
    if (!hasSid && !reg) {
      toast.error("Enter student ID or registration number.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      setValidated(true);
      return;
    }
    if (!(Challan_No || "").trim()) {
      toast.error("Challan number is required.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      setValidated(true);
      return;
    }

    const confirmMsg =
      "This will reverse ledger entries, mark the challan unpaid, and remove the bank scroll row. Continue?";
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setLoading(true);
    const data = {
      AccessKey,
      UserID: parseInt(UserID, 10) || 0,
      MemberType,
      Challan_No: (Challan_No || "").trim(),
      Student_ID: hasSid ? sid : 0,
      Registration_No: hasSid ? "" : reg,
      AdminRemarks: (AdminRemarks || "").trim(),
    };

    axios
      .post(config.base_url + "ScrollEntry/RollbackBankScrollPosting", data, {
        headers: { "Content-Type": "application/json" },
      })
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message || "Rollback completed.", {
            position: toast.POSITION.TOP_RIGHT,
          });
          setChallan_No("");
          setStudent_ID("");
          setRegistration_No("");
          setAdminRemarks("");
        } else {
          toast.error(res.data.status_message || "Rollback failed.", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(() => {
        toast.error("Network error.", { position: toast.POSITION.TOP_RIGHT });
      })
      .finally(() => {
        setLoading(false);
        setValidated(true);
      });
  };

  return (
    <>
      <RightsCheck />
      <ToastContainer />
      <FeeSystemMenu />

      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/FeeSystem/FeeSystemDashboard">Fee System</NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Rollback Bank Scroll
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Rollback Bank Scroll</div>
              </div>
              <div className="col-md-6">
                <div className="page-right-actions">
                  <NavLink
                    to="/FeeSystem/AddBankScroll"
                    className="button button-white button-icon"
                  >
                    Add Bank Scroll
                    <i className="ri-bank-line"></i>
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            {showHelp && (
              <div className="row mb-3">
                <div className="col-md-10 offset-md-1">
                  <div
                    className="alert alert-warning alert-dismissible fade show mb-0"
                    role="alert"
                    style={{ fontSize: "14px" }}
                  >
                    <strong>When to use:</strong> The bank rejected a deposit that was
                    already posted from <strong>Add Bank Scroll</strong>. This reverses
                    related ledger lines, marks the challan unpaid, and removes the scroll
                    entry. It targets the latest posted scroll for this challan. If fee
                    lines had other receipts mixed with the scroll, review amounts
                    manually after rollback.
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setShowHelp(false)}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-md-10 offset-md-1">
                <div className="card">
                  <div className="card-body card-form-wrap">
                    <Form noValidate validated={validated} onSubmit={handleSubmit}>
                      <div className="form-wrap">
                        <div className="form-legend">Rollback criteria</div>

                        <div className="row mb-3">
                          <label
                            htmlFor="rollbackChallanNo"
                            className="col-md-3 col-form-label col-form-label-sm"
                          >
                            Challan number:
                          </label>
                          <div className="col-md-4">
                            <input
                              id="rollbackChallanNo"
                              type="text"
                              className="form-control"
                              required
                              value={Challan_No}
                              onChange={(e) => setChallan_No(e.target.value)}
                              placeholder="As on fee challan"
                            />
                          </div>
                        </div>

                        <div className="row mb-3">
                          <label
                            htmlFor="rollbackStudentId"
                            className="col-md-3 col-form-label col-form-label-sm"
                          >
                            Student ID:
                          </label>
                          <div className="col-md-4">
                            <input
                              id="rollbackStudentId"
                              type="text"
                              className="form-control"
                              value={Student_ID}
                              onChange={(e) => setStudent_ID(e.target.value)}
                              placeholder="Numeric ID (optional if reg. no. below)"
                            />
                          </div>
                        </div>

                        <div className="row mb-3">
                          <label
                            htmlFor="rollbackRegNo"
                            className="col-md-3 col-form-label col-form-label-sm"
                          >
                            Registration number:
                          </label>
                          <div className="col-md-4">
                            <input
                              id="rollbackRegNo"
                              type="text"
                              className="form-control"
                              value={Registration_No}
                              onChange={(e) => setRegistration_No(e.target.value)}
                              placeholder="Use if student ID is not entered"
                            />
                          </div>
                        </div>

                        <div className="row mb-3">
                          <label
                            htmlFor="rollbackRemarks"
                            className="col-md-3 col-form-label col-form-label-sm"
                          >
                            Remarks (optional):
                          </label>
                          <div className="col-md-6">
                            <input
                              id="rollbackRemarks"
                              type="text"
                              className="form-control"
                              value={AdminRemarks}
                              onChange={(e) => setAdminRemarks(e.target.value)}
                              placeholder="Reason for rollback (audit)"
                            />
                          </div>
                        </div>

                        <div className="row mt-2">
                          <div className="col-12 text-end">
                            <button
                              type="submit"
                              className="button button-danger"
                              style={{ minWidth: "220px" }}
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                  />
                                  Processing…
                                </>
                              ) : (
                                "Rollback posted bank scroll"
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default RollbackBankScroll;
